import { cx } from "@linaria/core"
import { useEffect, useState } from "react"
import Markdown from "react-markdown"
import remarkGfm from "remark-gfm"


import { ColorSwatch } from "@/components/Markdown/ColorSwatch.tsx"
import { EmailMention } from "@/components/Markdown/EmailMention.tsx"
import { FileMention } from "@/components/Markdown/FileMention.tsx"
import {
  createMentionsPlugin,
  isEmailMention,
  isFileMention,
  isProfileMention
} from "@/components/Markdown/mentions.ts"
import { ProfileMention } from "@/components/Markdown/ProfileMention.tsx"
import { getLogger } from "@/lib/logging.ts"
import {
  Block,
  Blockquote,
  Code,
  HeadingStyles,
  Image,
  Li,
  Link,
  Markdown as MarkdownContainer,
  Ol,
  P,
  Pre,
  Strong,
  Table,
  Td,
  Th,
  Tr,
  Ul
} from "@/styles/Markdown.ts"
import { importHighlightJsStyles } from "@/styles/mixins.ts"

import type { DMDecoratedText } from "@/lib/client/generated/TypeDefinitions.ts"
import type { LinariaClassName } from "@linaria/core"
import type { ClassAttributes, HTMLAttributes, RefObject } from "react"
import type { ExtraProps } from "react-markdown"

// Mapping from file extensions to highlight.js modes that we actually load
const extensionToLanguage: Record<string, string> = {
  "aw": "php",
  "c++": "cpp",
  "cs": "csharp",
  "cshtml": "csharp",
  "csx": "csharp",
  "ctp": "php",
  "cxx": "cpp",
  "gql": "graphql",
  "gyp": "python",
  "gypi": "python",
  "h": "c",
  "h++": "cpp",
  "hpp": "cpp",
  "htm": "xml",
  "html": "xml",
  "hxx": "cpp",
  "ipp": "cpp",
  "ixx": "cpp",
  "js": "javascript",
  "jsf": "xml",
  "jspf": "xml",
  "jspx": "xml",
  "kt": "kotlin",
  "ktm": "kotlin",
  "kts": "kotlin",
  "lmi": "python",
  "md": "markdown",
  "mjs": "javascript",
  "mts": "typescript",
  "nse": "lua",
  "objc": "objectivec",
  "oc": "objectivec",
  "pd_lua": "lua",
  "php3": "php",
  "php4": "php",
  "php5": "php",
  "php7": "php",
  "phps": "php",
  "phpt": "php",
  "pl": "perl",
  "py": "python",
  "py3": "python",
  "pyde": "python",
  "pyi": "python",
  "pyp": "python",
  "pyt": "python",
  "pyw": "python",
  "rb": "ruby",
  "rbxs": "lua",
  "rlib": "rust",
  "rpy": "python",
  "rs": "rust",
  "sh": "bash",
  "st": "xml",
  "svg": "xml",
  "tag": "xml",
  "tagf": "xml",
  "tagx": "xml",
  "ts": "typescript",
  "tsx": "typescript",
  "txx": "cpp",
  "vb": "vbnet",
  "vbhtml": "xml",
  "wlua": "lua",
  "wsgi": "python",
  "xaml": "xml",
  "xht": "xml",
  "xhtml": "xml",
  "xjsp": "xml",
  "xpy": "python",
  "yml": "yaml",
}

const log = getLogger("MarkdownWrapper")

const inlineOnlyElements = [
  "text",
  "em",
  "strong",
  "del",
  "a",
  "code",
  "br",
  "span"
]

// Color code patterns
const COLOR_PATTERNS = [
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,           // hex: #fff, #ffffff, #ffffffff
  /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/i,                     // rgb(255, 255, 255) - legacy
  /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[\d.]+\s*\)$/i,       // rgba(255, 255, 255, 0.5) - legacy
  /^rgb\(\s*\d+\s+\d+\s+\d+(?:\s*\/\s*[\d.]+%?)?\s*\)$/i,        // rgb(255 255 255 / 0.5) - modern
  /^hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)$/i,                   // hsl(360, 100%, 50%) - legacy
  /^hsla\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*,\s*[\d.]+\s*\)$/i,     // hsla(360, 100%, 50%, 0.5) - legacy
  /^hsl\(\s*\d+(?:deg)?\s+\d+%\s+\d+%(?:\s*\/\s*[\d.]+%?)?\s*\)$/i, // hsl(360deg 100% 50% / 0.5) - modern
  /^oklch\(\s*[\d.]+%?\s+[\d.]+\s+[\d.]+(?:deg)?(?:\s*\/\s*[\d.]+%?)?\s*\)$/i, // oklch(59.69% 0.156 49.77deg / 0.5)
]

const isColorCode = (text: string): boolean => {
  return COLOR_PATTERNS.some(pattern => pattern.test(text.trim()))
}

const processTextWithColorCodes = (text: string): string => {
  // Replace color codes with inline code syntax so that Markdown processes them
  return text.replace(
    /(^|\s)(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|oklch\([^)]+\))(?=\s|$|[.,;:!?])/g,
    (_match, before, colorCode) => `${before}\`${colorCode}\``
  )
}

export const MarkdownWrapper = (props: {
  ref?: RefObject<HTMLDivElement | null>
  text: DMDecoratedText | string
  className?: LinariaClassName
  inlineOnly?: boolean
}) => {
  const originalText = typeof props.text === "string" ? props.text : props.text.value
  const text = processTextWithColorCodes(originalText)
  const inlineOnly = props.inlineOnly
  return (
    <div ref={props.ref} className={cx(MarkdownContainer, props.className)}>
      <Markdown
        children={text}
        allowedElements={props.inlineOnly ? inlineOnlyElements : undefined}
        unwrapDisallowed={props.inlineOnly}
        components={{
          a(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, href, ...rest } = props

            if (href && isFileMention(href)) {
              return <FileMention url={href} />
            } else if (href && isProfileMention(href)) {
              return <ProfileMention url={href} />
            } else if (href && isEmailMention(href)) {
              return <EmailMention url={href} />
            }

            return (
              <a {...rest} href={href} className={cx(Link, className)} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            )
          },
          blockquote(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <blockquote {...rest} className={cx(Blockquote, className)}>
                {children}
              </blockquote>
            )
          },
          code(props) {
            return <MarkdownCode {...props} inlineOnly={inlineOnly} />
          },
          h1(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h1 {...rest} className={cx(HeadingStyles.Heading1, className)}>
                {children}
              </h1>
            )
          },
          h2(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h2 {...rest} className={cx(HeadingStyles.Heading2, className)}>
                {children}
              </h2>
            )
          },
          h3(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h3 {...rest} className={cx(HeadingStyles.Heading3, className)}>
                {children}
              </h3>
            )
          },
          h4(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h4 {...rest} className={cx(HeadingStyles.Heading4, className)}>
                {children}
              </h4>
            )
          },
          h5(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h5 {...rest} className={cx(HeadingStyles.Heading5, className)}>
                {children}
              </h5>
            )
          },
          h6(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <h6 {...rest} className={cx(HeadingStyles.Heading6, className)}>
                {children}
              </h6>
            )
          },
          img(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <img {...rest} className={cx(Image, className)} alt="" loading="lazy">
                {children}
              </img>
            )
          },
          li(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <li {...rest} className={cx(Li, className)}>
                {children}
              </li>
            )
          },
          ol(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <ol {...rest} className={cx(Ol, className)}>
                {children}
              </ol>
            )
          },
          p(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props

            if (inlineOnly) {
              return children
            } else {
              return (
                <p {...rest} className={cx(P, className)}>
                  {children}
                </p>
              )
            }
          },
          pre(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <pre {...rest} className={cx(Pre, className)}>
                {children}
              </pre>
            )
          },
          strong(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <strong {...rest} className={cx(Strong, className)}>
                {children}
              </strong>
            )
          },
          table(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <table {...rest} className={cx(Block, Table, className)}>
                {children}
              </table>
            )
          },
          td(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <td {...rest} className={cx(Td, className)}>
                {children}
              </td>
            )
          },
          th(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <th {...rest} className={cx(Td, Th, className)}>
                {children}
              </th>
            )
          },
          tr(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <tr {...rest} className={cx(Tr, className)}>
                {children}
              </tr>
            )
          },
          ul(props) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { children, className, node, ...rest } = props
            return (
              <ul {...rest} className={cx(Ul, className)}>
                {children}
              </ul>
            )
          },
        }}
        remarkPlugins={[remarkGfm, createMentionsPlugin(props.text)]}
      />
    </div>
  )
}

const LANGUAGE_MARKER = /language-(\w+)/

const MarkdownCode = (props: ClassAttributes<HTMLElement> & HTMLAttributes<HTMLElement> & ExtraProps & {
  inlineOnly?: boolean
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { children, className, node, inlineOnly, ...rest } = props

  const code = String(children)
  const match = LANGUAGE_MARKER.exec(className || "")
  const [highlightedHtml, setHighlightedHtml] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!match || code.length >= 128 * 1024) return

    const lang = match[1]
    const resolvedLang = extensionToLanguage[lang] || lang

    let cleanup: () => void | undefined

    void import("highlight.js/lib/common").then((module) => {
      if (cancelled) return

      cleanup = importHighlightJsStyles()

      if (module.default.listLanguages().includes(resolvedLang)) {
        log.debug(`Highlighting code block with language ${resolvedLang}`, code)
        const highlighted = module.default.highlight(code, { language: resolvedLang })
        setHighlightedHtml(highlighted.value)
      }
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [code, match])

  if (!className?.includes("language-") && isColorCode(code)) {
    return <ColorSwatch color={code.trim()} />
  }

  if (highlightedHtml) {
    return (
      <code
        {...rest}
        className={cx(Code, className)}
        dangerouslySetInnerHTML={{ __html: highlightedHtml }}
      />
    )
  }

  return (
    <code {...rest} className={cx(Code, className)}>
      {children}
    </code>
  )
}
